/**
 * Clickable Normalization
 *
 * Normalizes clickable wrappers by keeping either button or link based on href validity.
 * Removes the unused element and ensures the kept element has data-hs-clickable="button".
 *
 * Structure:
 *   <div data-hs-clickable="wrapper">
 *     <button data-hs-clickable="button">Click</button>
 *     <a data-hs-clickable="button" href="/page">Click</a>
 *   </div>
 *
 * Logic: Keep link if href is valid, otherwise keep button.
 */
import { globalConfig, querySelectorAll } from '@utils';

interface ProcessedWrapper {
  wrapper: Element;
  keptElement: Element;
  wasLink: boolean;
  addedAttribute: boolean;
}

export async function init(): Promise<{ result: string; destroy: () => void }> {
  const cleanup: { processedWrappers: ProcessedWrapper[] } = {
    processedWrappers: [],
  };

  // Find all clickable wrappers using global clickable config
  const wrappers = querySelectorAll(
    globalConfig.clickable as {
      attributes: { elements: { [key: string]: { primary: string; aliases?: string[] } } };
    },
    'wrapper'
  );

  wrappers.forEach((wrapper) => {
    // Find direct button and link children
    const button = wrapper.querySelector(':scope > button');
    const link = wrapper.querySelector(':scope > a');

    // Only process if both exist
    if (!button || !link) return;

    // Check if link has valid href
    const href = link.getAttribute('href');
    const hasValidHref = href && href !== '' && href !== '#';

    let keptElement: Element;
    let removedElement: Element;

    if (hasValidHref) {
      // Keep link, remove button
      keptElement = link;
      removedElement = button;
    } else {
      // Keep button, remove link
      keptElement = button;
      removedElement = link;
    }

    // Check if kept element already has data-hs-clickable="button"
    const hadAttribute = keptElement.getAttribute('data-hs-clickable') === 'button';

    // Ensure kept element has data-hs-clickable="button"
    if (!hadAttribute) {
      keptElement.setAttribute('data-hs-clickable', 'button');
    }

    // Remove the other element
    removedElement.remove();

    // Track for cleanup
    cleanup.processedWrappers.push({
      wrapper,
      keptElement,
      wasLink: keptElement === link,
      addedAttribute: !hadAttribute,
    });
  });

  return {
    result: `clickable processed ${cleanup.processedWrappers.length} wrappers`,
    destroy: () => {},
  };
}
